import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    Search,
    UsersRound,
    X,
} from "lucide-react";
import {
    ChangeEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    useNavigate,
    useParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
    getAllInfinityMembers,
    getInfinityMembersByLevel,
} from "../../../../Redux/Teams/slice";
import { AppDispatch, RootState } from "../../../../store";
import {
    mapInfinityLevelToMilestone,
    mapInfinityMemberToNetworkMember,
    parseSamriddhiLevel,
} from "./SalarSamriddhiData";

import "./SalarSamriddhiPage.css";

const PAGE_SIZE = 10;

const SalarSamriddhiMilestonePage =
    () => {
        const navigate =
            useNavigate();
        const dispatch = useDispatch<AppDispatch>();
        const { level: levelParam, milestoneId } = useParams<{
            level?: string;
            milestoneId?: string;
        }>();
        const level = parseSamriddhiLevel(levelParam || milestoneId);
        const {
            infinityMembers = {},
            infinityMembersByLevel = [],
            infinityMembersByLevelPagination,
            loading,
        } = useSelector((state: RootState) => state.teams);
        const overviewRequestLevel = useRef<number | null>(null);
        const milestone = useMemo(() => {
            if (!level || !infinityMembers[level]) {
                return null;
            }

            return mapInfinityLevelToMilestone(level, infinityMembers[level]);
        }, [infinityMembers, level]);

        const [
            searchValue,
            setSearchValue,
        ] =
            useState("");

        const [
            currentPage,
            setCurrentPage,
        ] =
            useState(1);

        const milestoneRows = useMemo(
            () =>
                level
                    ? infinityMembersByLevel.map((member, index) =>
                        mapInfinityMemberToNetworkMember(member, level, index)
                    )
                    : [],
            [infinityMembersByLevel, level]
        );

        useEffect(() => {
            if (
                level &&
                !infinityMembers[level] &&
                overviewRequestLevel.current !== level
            ) {
                overviewRequestLevel.current = level;
                dispatch(getAllInfinityMembers({ page: 1, limit: PAGE_SIZE }));
            }
        }, [dispatch, infinityMembers, level]);

        useEffect(() => {
            if (level) {
                dispatch(
                    getInfinityMembersByLevel({
                        level,
                        page: currentPage,
                        limit: PAGE_SIZE,
                    })
                );
            }
        }, [currentPage, dispatch, level]);

        const filteredRows = useMemo(() => {
            const search = searchValue.trim().toLowerCase();

            if (!search) {
                return milestoneRows;
            }

            return milestoneRows.filter((record) =>
                record.searchText.includes(search)
            );
        }, [milestoneRows, searchValue]);

        const visibleRows = filteredRows;
        const totalPages = Math.max(
            1,
            infinityMembersByLevelPagination?.totalPages || 1
        );

        useEffect(() => {
            setCurrentPage((previous) => Math.min(previous, totalPages));
        }, [totalPages]);

        const pageNumbers = useMemo(
            () => Array.from({ length: totalPages }, (_, index) => index + 1),
            [totalPages]
        );
        const isSearching = searchValue.trim().length > 0;
        const totalResults = isSearching
            ? filteredRows.length
            : infinityMembersByLevelPagination?.totalMembers ?? milestoneRows.length;
        const firstResult = visibleRows.length
            ? isSearching
                ? 1
                : (currentPage - 1) * PAGE_SIZE + 1
            : 0;
        const lastResult = visibleRows.length
            ? isSearching
                ? visibleRows.length
                : firstResult + visibleRows.length - 1
            : 0;
        const networkMemberCount = isSearching
            ? filteredRows.length
            : infinityMembersByLevelPagination?.totalMembers ??
            milestone?.activeContributors ??
            milestoneRows.length;

        const handleSearch =
            (
                event: ChangeEvent<HTMLInputElement>
            ) => {
                setSearchValue(
                    event.target.value
                );

                setCurrentPage(
                    1
                );
            };

        const clearSearch =
            () => {
                setSearchValue(
                    ""
                );

                setCurrentPage(
                    1
                );
            };

        return (
            <section className="samriddhi-page">
                <div className="samriddhi-shell">
                    <div className="samriddhi-card">
                        <header className="samriddhi-header">
                            <div className="samriddhi-heading">
                                <button
                                    type="button"
                                    className="samriddhi-back-button"
                                    onClick={() =>
                                        navigate(
                                            "/InfinityCommunity"
                                        )
                                    }
                                    aria-label="Back to Salar Samriddhi"
                                >
                                    <ArrowLeft
                                        size={
                                            18
                                        }
                                        strokeWidth={
                                            2
                                        }
                                    />
                                </button>

                                <div>
                                    <span className="samriddhi-kicker">
                                        MY NETWORK
                                    </span>

                                    <h1>
                                        Salar Samriddhi{" "}
                                        {level
                                            ? `Milestone - ${level}`
                                            : "Milestone"}
                                    </h1>

                                    <p>
                                        View partner network members and their referring partner details.
                                    </p>
                                </div>
                            </div>

                            <div className="samriddhi-search">
                                <Search
                                    size={
                                        16
                                    }
                                    strokeWidth={
                                        1.9
                                    }
                                />

                                <input
                                    type="text"
                                    inputMode="search"
                                    value={
                                        searchValue
                                    }
                                    onChange={
                                        handleSearch
                                    }
                                    placeholder="Search network"
                                    aria-label="Search milestone network"
                                />

                                {searchValue && (
                                    <button
                                        type="button"
                                        onClick={
                                            clearSearch
                                        }
                                        aria-label="Clear search"
                                    >
                                        <X
                                            size={
                                                14
                                            }
                                            strokeWidth={
                                                2.2
                                            }
                                        />
                                    </button>
                                )}
                            </div>
                        </header>

                        <div className="samriddhi-summary-row">
                            <div className="samriddhi-summary">
                                <span>
                                    Network Members
                                </span>

                                <strong>
                                    {
                                        networkMemberCount
                                    }
                                </strong>
                            </div>

                            {milestone && (
                                <>
                                    <div className="samriddhi-summary">
                                        <span>
                                            Requirement
                                        </span>

                                        <strong>
                                            {
                                                milestone.contributionRequirement
                                            }
                                        </strong>
                                    </div>

                                    <div className="samriddhi-summary">
                                        <span>
                                            Active
                                        </span>

                                        <strong>
                                            {
                                                milestone.activeContributors
                                            }
                                        </strong>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="samriddhi-table-shell">
                            <div
                                className="samriddhi-table-scroll"
                                role="region"
                                aria-label="Salar Samriddhi milestone network table"
                                tabIndex={
                                    0
                                }
                            >
                                <table className="samriddhi-table samriddhi-detail-table">
                                    <thead>
                                        <tr>
                                            <th
                                                scope="col"
                                                className="samriddhi-sticky-column"
                                            >
                                                Milestone
                                            </th>

                                            <th scope="col">
                                                Partner Network ID
                                            </th>

                                            <th scope="col">
                                                Partner Network Name
                                            </th>

                                            <th scope="col">
                                                Referring Partner ID
                                            </th>

                                            <th scope="col">
                                                Referring Partner Name
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td
                                                    colSpan={
                                                        5
                                                    }
                                                    className="samriddhi-empty"
                                                >
                                                    <strong>
                                                        Loading...
                                                    </strong>
                                                </td>
                                            </tr>
                                        ) : visibleRows.length >
                                            0 ? (
                                            visibleRows.map(
                                                (
                                                    record
                                                ) => (
                                                    <tr
                                                        key={
                                                            record.id
                                                        }
                                                    >
                                                        <td className="samriddhi-sticky-column">
                                                            <span className="samriddhi-table-primary">
                                                                {
                                                                    record.milestone
                                                                }
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span className="samriddhi-network-id-badge">
                                                                {
                                                                    record.partnerNetworkId
                                                                }
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span className="samriddhi-table-primary">
                                                                {
                                                                    record.partnerNetworkName
                                                                }
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span className="samriddhi-network-id-badge neutral">
                                                                {
                                                                    record.referringPartnerId
                                                                }
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span className="samriddhi-table-primary">
                                                                {
                                                                    record.referringPartnerName
                                                                }
                                                            </span>
                                                        </td>
                                                    </tr>
                                                )
                                            )
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={
                                                        5
                                                    }
                                                    className="samriddhi-empty"
                                                >
                                                    <UsersRound
                                                        size={
                                                            27
                                                        }
                                                        strokeWidth={
                                                            1.7
                                                        }
                                                    />

                                                    <strong>
                                                        No network members found
                                                    </strong>

                                                    <span>
                                                        Try a different search term.
                                                    </span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <footer className="samriddhi-footer">
                            <span className="samriddhi-result-count">
                                Showing{" "}
                                <strong>
                                    {
                                        firstResult
                                    }
                                </strong>{" "}
                                -{" "}
                                <strong>
                                    {
                                        lastResult
                                    }
                                </strong>{" "}
                                of{" "}
                                <strong>
                                    {
                                        totalResults
                                    }
                                </strong>
                            </span>

                            <div className="samriddhi-pagination">
                                <button
                                    type="button"
                                    className="samriddhi-pagination-arrow"
                                    disabled={
                                        currentPage ===
                                        1
                                    }
                                    onClick={() =>
                                        setCurrentPage(
                                            (
                                                previous
                                            ) =>
                                                Math.max(
                                                    1,
                                                    previous -
                                                    1
                                                )
                                        )
                                    }
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft
                                        size={
                                            16
                                        }
                                        strokeWidth={
                                            2
                                        }
                                    />
                                </button>

                                {pageNumbers.map(
                                    (
                                        page
                                    ) => (
                                        <button
                                            type="button"
                                            className={`samriddhi-pagination-page ${currentPage ===
                                                    page
                                                    ? "active"
                                                    : ""
                                                }`}
                                            key={
                                                page
                                            }
                                            onClick={() =>
                                                setCurrentPage(
                                                    page
                                                )
                                            }
                                            aria-current={
                                                currentPage ===
                                                    page
                                                    ? "page"
                                                    : undefined
                                            }
                                        >
                                            {
                                                page
                                            }
                                        </button>
                                    )
                                )}

                                <button
                                    type="button"
                                    className="samriddhi-pagination-arrow"
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                    onClick={() =>
                                        setCurrentPage(
                                            (
                                                previous
                                            ) =>
                                                Math.min(
                                                    totalPages,
                                                    previous +
                                                    1
                                                )
                                        )
                                    }
                                    aria-label="Next page"
                                >
                                    <ChevronRight
                                        size={
                                            16
                                        }
                                        strokeWidth={
                                            2
                                        }
                                    />
                                </button>
                            </div>
                        </footer>
                    </div>
                </div>
            </section>
        );
    };

export default SalarSamriddhiMilestonePage;
